import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { BASE_API_URL } from '../utils/api';
import toast from 'react-hot-toast';
import { 
  requestNotificationPermission, 
  onForegroundMessage,
  areNotificationsEnabled,
  signInWithGoogle as firebaseSignInWithGoogle
} from '../config/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [lastActivity, setLastActivity] = useState(() => {
    // Check if there's a stored last activity time
    const stored = localStorage.getItem('lastActivity');
    return stored ? parseInt(stored, 10) : Date.now();
  });
  const [fcmToken, setFcmToken] = useState(null);
  
  // Auto logout after 30 minutes of inactivity
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

  // Configure axios defaults and interceptor
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }

    // Add response interceptor to handle 401 errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token invalid or expired - logout user
          localStorage.removeItem('token');
          localStorage.removeItem('lastActivity');
          setToken(null);
          setUser(null);
          toast.error('Session expired. Please login again.');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [token]);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        // Check if session expired due to inactivity before loading user
        const storedLastActivity = localStorage.getItem('lastActivity');
        if (storedLastActivity) {
          const timeSinceLastActivity = Date.now() - parseInt(storedLastActivity, 10);
          if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
            // Session expired - logout
            localStorage.removeItem('token');
            localStorage.removeItem('lastActivity');
            setToken(null);
            setUser(null);
            setLoading(false);
            toast.error('Session expired due to inactivity. Please login again.');
            window.location.href = '/login';
            return;
          }
        }

        try {
          const response = await axios.get(`${BASE_API_URL}/auth/me`);
          setUser(response.data.user);
          // Update lastActivity on successful load
          const now = Date.now();
          setLastActivity(now);
          localStorage.setItem('lastActivity', now.toString());
        } catch (error) {
          console.error('Failed to load user:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('lastActivity');
          setToken(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  // Function to refresh user data
  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${BASE_API_URL}/auth/me`);
        setUser(response.data.user);
        return true;
      } catch (error) {
        console.error('Failed to refresh user:', error);
        return false;
      }
    }
    return false;
  };

  // Register FCM token when user logs in
  useEffect(() => {
    if (!user || fcmToken) return;

    const registerFCMToken = async () => {
      try {
        // Request notification permission and get FCM token
        const token = await requestNotificationPermission();
        
        if (token) {
          setFcmToken(token);
          
          // Register token with backend
          await axios.post(`${BASE_API_URL}/notifications/register-device`, {
            fcmToken: token
          });
          
          console.log('FCM token registered successfully');
          // toast.success('🔔 Push notifications enabled!', { duration: 2000 });
        } else {
          console.log('FCM token not available');
        }
      } catch (error) {
        console.error('Failed to register FCM token:', error);
      }
    };

    // Ask for permission after a short delay (better UX)
    const timer = setTimeout(() => {
      registerFCMToken();
    }, 2000);

    return () => clearTimeout(timer);
  }, [user, fcmToken]);

  // Listen for foreground messages
  useEffect(() => {
    if (!user) return;

    const unsubscribe = onForegroundMessage((notification) => {
      // Show toast notification
      toast.custom(
        (t) => (
          <div
            className={`${
              t.visible ? 'animate-enter' : 'animate-leave'
            } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
            onClick={() => {
              if (notification.actionUrl) {
                window.location.href = notification.actionUrl;
              }
              toast.dismiss(t.id);
            }}
          >
            <div className="flex-1 w-0 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-600 text-xl">🔔</span>
                  </div>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {notification.title}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {notification.body}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex border-l border-gray-200">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toast.dismiss(t.id);
                }}
                className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-primary-600 hover:text-primary-500 focus:outline-none"
              >
                Close
              </button>
            </div>
          </div>
        ),
        { duration: 5000 }
      );
    });

    return unsubscribe;
  }, [user]);

  // Track user activity
  useEffect(() => {
    if (!user) return;

    const updateActivity = () => {
      const now = Date.now();
      setLastActivity(now);
      localStorage.setItem('lastActivity', now.toString());
    };
    
    // Track various user activities
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [user]);

  // Check for inactivity and auto logout
  useEffect(() => {
    if (!user) return;

    const checkInactivity = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;

      if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
        localStorage.removeItem('token');
        localStorage.removeItem('lastActivity');
        setToken(null);
        setUser(null);
        toast.error('You have been logged out due to inactivity');
        window.location.href = '/';
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(checkInactivity);
  }, [user, lastActivity, INACTIVITY_TIMEOUT]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${BASE_API_URL}/auth/login`, {
        username,
        password
      });

      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      const now = Date.now();
      localStorage.setItem('lastActivity', now.toString());
      setToken(newToken);
      setUser(userData);
      setLastActivity(now);
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      const isUnverified = error.response?.data?.isUnverified || false;
      const email = error.response?.data?.email || '';
      const phone = error.response?.data?.phone || '';
      const userId = error.response?.data?.userId || '';
      
      if (!isUnverified) {
        toast.error(message);
      }
      
      return { 
        success: false, 
        message,
        isUnverified,
        email,
        phone,
        userId,
        username
      };
    }
  };

  const register = async (formData) => {
    try {
      const response = await axios.post(`${BASE_API_URL}/auth/register`, formData);

      // New flow: No token returned, email verification required
      // Return email and username for CheckEmailPage
      toast.success('Registration successful! Please check your email.');
      return {
        success: true,
        email: response.data.email,
        username: response.data.username
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    setToken(null);
    setUser(null);
    toast.success('Logged out successfully');
  };

  const updateProfile = async (data) => {
    try {
      const response = await axios.put(`${BASE_API_URL}/auth/profile`, data);
      setUser(response.data.user);
      toast.success('Profile updated successfully');
      return true;
    } catch (error) {
      const message = error.response?.data?.message || 'Update failed';
      toast.error(message);
      return false;
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.put(`${BASE_API_URL}/auth/change-password`, {
        currentPassword,
        newPassword
      });
      toast.success('Password changed successfully');
      return true;
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      return false;
    }
  };

  const resendVerification = async (email) => {
    try {
      const response = await axios.post(`${BASE_API_URL}/auth/resend-verification`, { email });
      toast.success(response.data.message);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to resend verification email';
      toast.error(message);
      return { success: false, message };
    }
  };

  const updateEmail = async (currentEmail, newEmail) => {
    try {
      const response = await axios.put(`${BASE_API_URL}/auth/update-email`, {
        currentEmail,
        newEmail
      });
      toast.success(response.data.message);
      return { success: true, email: response.data.email };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update email';
      toast.error(message);
      return { success: false, message };
    }
  };

  const loginWithGoogle = async () => {
    try {
      const googleUser = await firebaseSignInWithGoogle();
      
      // Send Google ID token to backend for verification
      const response = await axios.post(`${BASE_API_URL}/auth/google-login`, {
        idToken: googleUser.idToken,
        email: googleUser.email,
        displayName: googleUser.displayName,
        photoURL: googleUser.photoURL
      });

      const { token: jwtToken, user: userData, profileComplete } = response.data;
      
      setToken(jwtToken);
      localStorage.setItem('token', jwtToken);
      setUser(userData);
      
      toast.success(`Welcome ${userData.fullName || userData.username}!`);
      return { success: true, profileComplete };
    } catch (error) {
      console.error('Google login error:', error);
      const message = error.response?.data?.message || error.message || 'Google login failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const completeProfile = async (fullName, phone) => {
    try {
      const response = await axios.post(`${BASE_API_URL}/auth/complete-profile`, {
        fullName,
        phone
      });
      
      toast.success(response.data.message);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to complete profile';
      toast.error(message);
      return { success: false, message };
    }
  };

  const verifyProfileOtp = async (otp) => {
    try {
      const response = await axios.post(`${BASE_API_URL}/auth/verify-profile-otp`, {
        otp
      });
      
      // Update user with completed profile
      setUser(response.data.user);
      
      toast.success(response.data.message);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'OTP verification failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    resendVerification,
    updateEmail,
    loginWithGoogle,
    completeProfile,
    verifyProfileOtp,
    refreshUser, // Add refreshUser
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
